import { useMemo } from 'react'

import { usePRFileSections } from '@sketch-hq/sketch-web-renderer'
import { useGetSectionsForPageQuery } from '@sketch/gql-types'

import { useVersioning } from '../../versioning'
import { getSiblingSections } from '../../shares/SectionView/utils/getSiblingSections'
import { getNearestSectionAncestorUUID } from '@sketch/canvas'
import { useCanvasHost } from './useCanvasHost'

export interface CanvasSection {
  uuid: string
  name: string | null
}

/**
 * The siblings of the Section currently in focus - the Sections sharing its
 * nearest Section ancestor, or all top-level ones when it has none. A Section's
 * own children are reached by navigating into it, not from this list.
 *
 * Both hosts answer the same question from different places, and this is where
 * that choice is made: a Share asks GraphQL, which resolves as soon as the
 * network does, while a Preview has no share to query and reads the parsed
 * presentation file instead, which knows how the Sections nest.
 */
export function useCanvasSiblingSections(
  pageUUID: string,
  currentSectionUUID: string
): CanvasSection[] {
  const host = useCanvasHost()
  const { versionShortId } = useVersioning()
  const isShare = host?.kind === 'share'

  const { data } = useGetSectionsForPageQuery({
    variables: {
      identifier: host?.identifier ?? '',
      versionShortId,
      pageUUID,
    },
    skip: !isShare || !pageUUID,
  })

  // Called unconditionally: in a Share the file may not be parsed yet, and the
  // hook has to run either way.
  const fileSections = usePRFileSections()

  const shareSections = data?.share?.version?.document?.page?.sections

  return useMemo(() => {
    if (isShare) {
      return getSiblingSections(shareSections ?? [], currentSectionUUID)
    }

    const sections = fileSections ?? []
    const currentParentUUID = getNearestSectionAncestorUUID(
      sections
        .find(section => section.objectId === currentSectionUUID)
        ?.getNode() ?? null
    )

    return sections
      .filter(
        section =>
          getNearestSectionAncestorUUID(section.getNode()) === currentParentUUID
      )
      .map(section => ({ uuid: section.objectId, name: section.name }))
  }, [isShare, shareSections, fileSections, currentSectionUUID])
}
